import { X, Save, AlertCircle, CheckCircle, GitCompare } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import Editor, { DiffEditor } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';

interface IngressYAMLEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialYAML: string;
  onSave: (yaml: string, commitMessage: string) => Promise<void>;
  isLoading?: boolean;
}

interface YAMLValidationError {
  line?: number;
  message: string;
}

export default function IngressYAMLEditorModal({
  isOpen,
  onClose,
  initialYAML,
  onSave,
  isLoading = false
}: IngressYAMLEditorModalProps) {
  const [yamlContent, setYamlContent] = useState(initialYAML);
  const [commitMessage, setCommitMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<YAMLValidationError[]>([]);
  const [isValid, setIsValid] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setYamlContent(initialYAML);
      setCommitMessage('');
      setHasChanges(false);
      setValidationErrors([]);
      setIsValid(true);
      setShowReviewModal(false);
    }
  }, [isOpen, initialYAML]);

  const handleClose = () => {
    // Close without confirmation - discard changes
    setShowReviewModal(false);
    onClose();
  };

  // Validate YAML in real-time
  const validateYAML = (yaml: string): YAMLValidationError[] => {
    const errors: YAMLValidationError[] = [];

    // Basic syntax checks
    if (!yaml.trim()) {
      errors.push({ message: 'YAML content cannot be empty' });
      return errors;
    }

    // Check for common YAML issues
    const lines = yaml.split('\n');

    // Check for tabs (YAML doesn't allow tabs for indentation)
    lines.forEach((line, index) => {
      if (line.includes('\t')) {
        errors.push({
          line: index + 1,
          message: 'YAML does not allow tabs for indentation. Use spaces instead.'
        });
      }
    });

    // Check for duplicate keys at same level (simplified check)
    const seenKeys = new Set<string>();
    let currentIndent = 0;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;

      const keyMatch = trimmed.match(/^([^:]+):/);
      if (keyMatch) {
        const key = keyMatch[1].trim();
        const indent = line.length - line.trimStart().length;

        // Reset seen keys if indent changes
        if (indent !== currentIndent) {
          seenKeys.clear();
          currentIndent = indent;
        }

        if (seenKeys.has(key)) {
          errors.push({
            line: index + 1,
            message: `Duplicate key: "${key}"`
          });
        }
        seenKeys.add(key);
      }
    });

    // Check for required Kubernetes fields
    if (!yaml.includes('apiVersion:')) {
      errors.push({ message: 'Missing required field: apiVersion' });
    }
    if (!yaml.includes('kind:')) {
      errors.push({ message: 'Missing required field: kind' });
    }
    if (!yaml.includes('metadata:')) {
      errors.push({ message: 'Missing required field: metadata' });
    }
    if (!yaml.includes('spec:')) {
      errors.push({ message: 'Missing required field: spec' });
    }

    return errors;
  };

  const handleYAMLChange = (newYAML: string | undefined) => {
    if (newYAML === undefined) return;

    setYamlContent(newYAML);
    setHasChanges(newYAML !== initialYAML);

    // Validate YAML
    const errors = validateYAML(newYAML);
    setValidationErrors(errors);
    setIsValid(errors.length === 0);

    // Update Monaco Editor markers
    updateEditorMarkers(errors);
  };

  // Handle editor mount
  const handleEditorDidMount = (editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Set editor options
    editor.updateOptions({
      tabSize: 2,
      insertSpaces: true,
      detectIndentation: false,
    });

    // Initial validation
    const errors = validateYAML(yamlContent);
    updateEditorMarkers(errors);
  };

  // Update Monaco Editor error markers
  const updateEditorMarkers = (errors: YAMLValidationError[]) => {
    if (!monacoRef.current || !editorRef.current) return;

    const monaco = monacoRef.current;
    const model = editorRef.current.getModel();
    if (!model) return;

    const markers: Monaco.editor.IMarkerData[] = errors
      .filter(error => error.line !== undefined)
      .map(error => ({
        startLineNumber: error.line!,
        startColumn: 1,
        endLineNumber: error.line!,
        endColumn: model.getLineMaxColumn(error.line!),
        message: error.message,
        severity: monaco.MarkerSeverity.Error,
      }));

    monaco.editor.setModelMarkers(model, 'yaml-validation', markers);
  };

  const handleReviewClick = () => {
    if (!hasChanges) {
      toast.error('No changes to review');
      return;
    }

    if (!isValid) {
      toast.error('Please fix validation errors before proceeding');
      return;
    }

    // Show review modal with diff
    setShowReviewModal(true);
  };

  const handleCommit = async () => {
    if (!commitMessage.trim()) {
      toast.error('Please enter a commit message');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(yamlContent, commitMessage);
      toast.success('YAML updated and committed to GitLab');
      setShowReviewModal(false);
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save YAML');
    } finally {
      setIsSaving(false);
    }
  };

  const lineCount = yamlContent.split('\n').length;
  const charCount = yamlContent.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-orange-100 rounded-lg">
                <svg className="w-5 h-5 text-brand-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Edit Ingress YAML</h2>
                <p className="text-sm text-gray-500">Manually edit the Kubernetes ingress manifest</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isSaving}
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Validation Errors Banner */}
          {validationErrors.length > 0 && (
            <div className="p-4 bg-red-50 border-b border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    {validationErrors.length} Validation Error{validationErrors.length > 1 ? 's' : ''}
                  </p>
                  <ul className="space-y-1">
                    {validationErrors.slice(0, 5).map((error, index) => (
                      <li key={index} className="text-xs text-red-700">
                        {error.line ? `Line ${error.line}: ` : ''}{error.message}
                      </li>
                    ))}
                    {validationErrors.length > 5 && (
                      <li className="text-xs text-red-600 font-medium">
                        +{validationErrors.length - 5} more errors...
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Warning Banner */}
          {hasChanges && isValid && (
            <div className="p-4 bg-yellow-50 border-b border-yellow-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    Unsaved Changes Detected
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Make sure to review your changes carefully before committing. Invalid YAML will break the ingress configuration.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success Banner */}
          {hasChanges && isValid && validationErrors.length === 0 && (
            <div className="p-3 bg-green-50 border-b border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-800">
                  YAML validation passed. Ready to commit.
                </p>
              </div>
            </div>
          )}

          {/* Editor */}
          <div className="flex-1 overflow-hidden p-6 flex flex-col gap-4 min-h-0">
            {/* YAML Editor */}
            <div className="h-[550px] border border-gray-300 rounded-lg overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-full bg-gray-900">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-brand-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-gray-300 text-sm font-medium">Loading YAML content...</p>
                    <p className="text-gray-500 text-xs mt-1">Fetching from GitLab</p>
                  </div>
                </div>
              ) : (
                <Editor
                  key={`editor-${initialYAML?.substring(0, 50)}`}
                  height="100%"
                  defaultLanguage="yaml"
                  defaultValue={initialYAML}
                  onChange={handleYAMLChange}
                  onMount={handleEditorDidMount}
                  theme="vs-dark"
                options={{
                  readOnly: isSaving,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: true,
                  fontSize: 14,
                  lineNumbers: 'on',
                  padding: { top: 16, bottom: 16 },
                  renderWhitespace: 'all',
                  folding: true,
                  showFoldingControls: 'always',
                  foldingStrategy: 'indentation',
                  foldingHighlight: true,
                  unfoldOnClickAfterEndOfLine: true,
                  automaticLayout: true,
                  wordWrap: 'on',
                  tabSize: 2,
                  insertSpaces: true,
                  detectIndentation: false,
                  glyphMargin: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    verticalScrollbarSize: 10,
                    horizontalScrollbarSize: 10,
                    useShadows: true,
                  },
                  suggest: {
                    showKeywords: true,
                    showSnippets: true,
                  },
                  quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: false,
                  },
                  formatOnPaste: true,
                  formatOnType: true,
                  renderLineHighlight: 'all',
                  cursorBlinking: 'smooth',
                  smoothScrolling: true,
                  bracketPairColorization: {
                    enabled: true,
                  },
                }}
                loading={
                  <div className="flex items-center justify-center h-full bg-gray-900">
                    <div className="text-center">
                      <div className="w-8 h-8 border-4 border-brand-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-gray-400 text-sm">Initializing editor...</p>
                    </div>
                  </div>
                }
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>Lines: {lineCount}</span>
              <span>Characters: {charCount}</span>
              {hasChanges && (
                <span className="flex items-center gap-1 text-orange-600 font-medium">
                  <AlertCircle className="w-3 h-3" />
                  Unsaved changes
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReviewClick}
                disabled={!hasChanges || isSaving || !isValid}
                className="flex items-center gap-2 px-4 py-2 bg-brand-blue-600 hover:bg-brand-blue-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                title="Review changes and add commit message"
              >
                <GitCompare className="w-4 h-4" />
                <span>Review & Save</span>
              </button>
            </div>
          </div>
        </div>

        {/* Review & Save Modal - Combined Diff + Commit Message */}
        {showReviewModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 rounded-xl">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-blue-100 rounded-lg">
                    <GitCompare className="w-6 h-6 text-brand-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Review & Commit Changes
                    </h3>
                    <p className="text-sm text-gray-600">
                      Review differences and add commit message
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReviewModal(false)}
                  disabled={isSaving}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Warning Banner */}
              <div className="p-4 bg-orange-50 border-b border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-orange-800 font-medium mb-1">
                      ⚠️ Warning: This affects production traffic!
                    </p>
                    <p className="text-xs text-orange-700">
                      Changes will be committed to GitLab immediately and synced to Kubernetes cluster.
                    </p>
                  </div>
                </div>
              </div>

              {/* Diff Editor */}
              <div className="flex-1 p-6 overflow-hidden">
                <div className="h-[450px] border border-gray-300 rounded-lg overflow-hidden mb-4">
                  <DiffEditor
                    height="100%"
                    language="yaml"
                    original={initialYAML}
                    modified={yamlContent}
                    theme="vs-dark"
                    options={{
                      readOnly: true,
                      renderSideBySide: true,
                      fontSize: 13,
                      lineNumbers: 'on',
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      wordWrap: 'on',
                      renderWhitespace: 'selection',
                      diffWordWrap: 'on',
                      renderOverviewRuler: false,
                      overviewRulerBorder: false,
                      hideCursorInOverviewRuler: true,
                      scrollbar: {
                        vertical: 'visible',
                        horizontal: 'visible',
                        verticalScrollbarSize: 10,
                        horizontalScrollbarSize: 10,
                      },
                    }}
                    loading={
                      <div className="flex items-center justify-center h-full bg-gray-900">
                        <div className="text-center">
                          <div className="w-8 h-8 border-4 border-brand-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                          <p className="text-gray-400 text-sm">Loading diff...</p>
                        </div>
                      </div>
                    }
                  />
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 text-xs text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                    <span>Deleted lines</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                    <span>Added lines</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                    <span>Modified lines</span>
                  </div>
                </div>

                {/* Commit Message Input */}
                <div className="bg-white rounded-lg p-4 border border-gray-300">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commit Message <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    placeholder="e.g., Update ingress routes for tenant acme"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                    disabled={isSaving}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Describe what changes you made and why
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">
                  ⚠️ Changes will be committed to GitLab and automatically deployed to production
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowReviewModal(false)}
                    disabled={isSaving}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50"
                  >
                    Back to Edit
                  </button>
                  <button
                    onClick={handleCommit}
                    disabled={!commitMessage.trim() || isSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-orange-600 hover:bg-brand-orange-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Committing...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Commit Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
